import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { BASE_API_URL } from 'core/constants';
import { API_URLS } from 'core/endpoints';

import {
  getAccessToken,
  getRefreshToken,
  willTokenExpiredUnderNMinutes,
  removeTokens,
  updateAccessToken,
  getEphemeralToken
} from 'core/helpers/token';
import { toast } from 'react-toastify';

// Timeout configuration for different request types
const DEFAULT_TIMEOUT = 30000; // 30 seconds for normal API calls
export const STREAMING_TIMEOUT = 1000 * 60 * 5; // 5 minutes for streaming/SSE
export const UPLOAD_TIMEOUT = 1000 * 60 * 10; // 10 minutes for file uploads

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  accept: 'application/json',
  'local-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
};

const getAuthorizationString = (token: string) => `Bearer ${token}`;

// ============================================================================
// Request Deduplication - Prevents duplicate simultaneous GET requests
// ============================================================================
const pendingRequests = new Map<string, Promise<any>>();

const BaseAxios = axios.create({
  baseURL: BASE_API_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: { ...COMMON_HEADERS }
});

const API = axios.create({
  baseURL: BASE_API_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: { ...COMMON_HEADERS }
});

API.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers['Authorization'] = getAuthorizationString(token);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

type TokenRefreshCallback = (accessToken: string | null) => void;

let isRefreshing = false;
let refreshSubscribers: TokenRefreshCallback[] = [];

const subscribeTokenRefresh = (callback: TokenRefreshCallback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (accessToken: string | null) => {
  refreshSubscribers.forEach((callback) => callback(accessToken));
};

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Refresh token is not available');
  }

  const payload = { refresh_token: refreshToken };
  try {
    const res = await axios.post(API_URLS.refreshToken, payload);
    const accessToken = res.data?.access_token;
    const ephemeralToken = res.data?.ephemeral_token;

    updateAccessToken(accessToken, ephemeralToken);

    return { accessToken, ephemeralToken };
  } catch (error) {
    removeTokens();
    window.location.href = '/logout';
    throw new Error('Unable to refresh the token');
  }
};

API.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      if (!getRefreshToken()) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((accessToken) => {
            if (!accessToken) {
              reject(error);
              return;
            }

            originalRequest.headers['Authorization'] = getAuthorizationString(accessToken);
            resolve(API(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const { accessToken } = await refreshAccessToken();
        onRefreshed(accessToken);
        originalRequest.headers['Authorization'] = getAuthorizationString(accessToken);
        return API(originalRequest);
      } catch (err) {
        onRefreshed(null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
        refreshSubscribers = [];
      }
    }
    return Promise.reject(error);
  }
);

const getAccessTokenThatMinValidForNMinutes = async (minutes: number) => {
  let accessToken = getAccessToken();
  let ephemeralToken = getEphemeralToken();

  if (willTokenExpiredUnderNMinutes(accessToken, minutes)) {
    const tokens = await refreshAccessToken();
    accessToken = tokens.accessToken;
    ephemeralToken = tokens.ephemeralToken;
  }

  return { accessToken, ephemeralToken };
};

export function getServerErrorMessage(err: AxiosError) {
  const status = err?.response?.status || 400;
  const responseData: any = err.response?.data;

  let message: string | null = null;

  if (status == 422) {
    const detail = responseData?.detail;
    if (Array.isArray(detail) && detail.length > 0) {
      message = detail[0]?.msg?.replace(/^Value error, /, '') || 'Validation error';
    } else if (typeof detail === 'string') {
      message = detail;
    } else {
      message = 'Data is not valid. Please check your input.';
    }
  } else if (status >= 400 && status < 500) {
    message = responseData?.detail;
  } else if (status > 500) {
    message = 'Something is wrong with the server. Try later or contact us!';
  }

  if (status == 401) message = null;

  return { status, message };
}

export function toastApiError(err: AxiosError | any, defaultMessage: string | null = null) {
  const { message } = getServerErrorMessage(err);

  if (message || defaultMessage) {
    toast.error(message || defaultMessage);
  }
}

export async function getStreamHeaders() {
  const token_padding_minutes = 5;
  const { accessToken } = await getAccessTokenThatMinValidForNMinutes(token_padding_minutes);
  const headers = {
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'local-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  return headers;
}

/**
 * Deduplicated GET request - prevents duplicate simultaneous requests
 * If an identical GET request is already in flight, returns the same promise
 */
export function deduplicatedGet<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const key = `get-${url}-${JSON.stringify(config?.params || {})}`;

  // Return existing promise if request is in flight
  const existingPromise = pendingRequests.get(key);
  if (existingPromise) {
    return existingPromise as Promise<T>;
  }

  // Create and store the promise
  const promise: Promise<T> = API.get(url, config)
    .then((response) => {
      pendingRequests.delete(key);
      return response.data as T;
    })
    .catch((error) => {
      pendingRequests.delete(key);
      throw error;
    });

  pendingRequests.set(key, promise);
  return promise;
}

/**
 * Clear all pending deduplicated requests
 * Useful when navigating away from a page
 */
export function clearPendingRequests(): void {
  pendingRequests.clear();
}

export { API, BaseAxios, getAccessTokenThatMinValidForNMinutes };
